import { useQuery, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { ANALYTICS_DATA_STALE_TIME_MS } from 'app/modules/analytics/config/analyticsConfig';
import NewAnalyticsApi from 'api/new-analytics';
import { useAnalyticsQueryDescriptor } from 'app/modules/analytics/hooks/useAnalyticsQueryDescriptor';
import {
  analyticsDescriptorKey,
  hasAnalyticsQueryablePages,
} from 'app/modules/analytics/utils/analyticsQueryDescriptor';
import {
  isAudienceResponseIncomplete,
  markAnalyticsQueryStaleIfEmpty,
} from 'app/modules/analytics/utils/analyticsIncompleteResponseRefetch';
import { ApiClientError } from 'api/client';
import { useToast } from 'app/hooks/useToast';
import type { AudienceResponse } from 'api/new-analytics/types';

export function useAudienceQuery() {
  const descriptor = useAnalyticsQueryDescriptor();
  const { pageIds, since, until, timezone } = descriptor;
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const audienceQueryKey = analyticsDescriptorKey.audience(descriptor);

  return useQuery<AudienceResponse, ApiClientError>(
    audienceQueryKey,
    () =>
      NewAnalyticsApi.getAudience({
        page_ids: pageIds,
        since,
        until,
        timezone,
      }),
    {
      keepPreviousData: true,
      staleTime: ANALYTICS_DATA_STALE_TIME_MS,
      enabled:
        hasAnalyticsQueryablePages(descriptor) &&
        dayjs(since).isValid() &&
        dayjs(until).isValid(),
      onSuccess: (data) => {
        markAnalyticsQueryStaleIfEmpty(
          queryClient,
          audienceQueryKey,
          isAudienceResponseIncomplete(data),
        );
      },
      onError: (e) => {
        toast(e?.userMessage ?? t('somethingWentWrong'), 'error');
      },
    },
  );
}
